import { Controller } from "@hotwired/stimulus"

const STATUS_ORDER = { delivered: 0, shipped: 1, submitted: 2, pending: 3, failed: 4 }

const SORT_ICON_NEUTRAL = '<path stroke-linecap="round" stroke-linejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />'
const SORT_ICON_ASC = '<path stroke-linecap="round" stroke-linejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75L17.25 9m0 0L21 12.75M17.25 9v12" />'
const SORT_ICON_DESC = '<path stroke-linecap="round" stroke-linejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0-3.75-3.75M17.25 21 21 17.25" />'

export default class extends Controller {
  static targets = ["row", "prevBtn", "nextBtn", "pageInfo", "sortBtn", "tbody"]
  static values = {
    pageSize: { type: Number, default: 10 },
    sortField: { type: String, default: "" },
    sortDir: { type: String, default: "asc" }
  }

  connect() {
    this.currentPage = 0
    this.updateView()
  }

  sort(event) {
    const field = event.currentTarget.dataset.sortField
    if (this.sortFieldValue === field) {
      this.sortDirValue = this.sortDirValue === "asc" ? "desc" : "asc"
    } else {
      this.sortFieldValue = field
      this.sortDirValue = "asc"
    }
    this.currentPage = 0
    this.sortRows()
  }

  sortRows() {
    const rows = [...this.rowTargets]
    const dir = this.sortDirValue === "asc" ? 1 : -1

    rows.sort((a, b) => {
      let av, bv
      switch (this.sortFieldValue) {
        case "status":
          av = STATUS_ORDER[a.dataset.status] ?? 99
          bv = STATUS_ORDER[b.dataset.status] ?? 99
          return dir * (av - bv)
        case "qr_scan":
          av = parseInt(a.dataset.qrScan) || 0
          bv = parseInt(b.dataset.qrScan) || 0
          return dir * (av - bv)
        case "video_request":
          av = parseInt(a.dataset.videoRequest) || 0
          bv = parseInt(b.dataset.videoRequest) || 0
          return dir * (av - bv)
        case "consultation":
          av = parseInt(a.dataset.consultation) || 0
          bv = parseInt(b.dataset.consultation) || 0
          return dir * (av - bv)
        default:
          return 0
      }
    })

    const tbody = this.tbodyTarget
    rows.forEach(row => tbody.appendChild(row))

    // Update sort button icons
    this.sortBtnTargets.forEach(btn => {
      const field = btn.dataset.sortField
      const icon = btn.querySelector("[data-sort-indicator]")
      if (!icon) return
      if (field === this.sortFieldValue) {
        icon.innerHTML = this.sortDirValue === "asc" ? SORT_ICON_ASC : SORT_ICON_DESC
        icon.classList.remove("text-base-content/20")
        icon.classList.add("text-primary")
      } else {
        icon.innerHTML = SORT_ICON_NEUTRAL
        icon.classList.remove("text-primary")
        icon.classList.add("text-base-content/20")
      }
    })

    this.updateView()
  }

  nextPage() {
    const totalPages = Math.ceil(this.rowTargets.length / this.pageSizeValue)
    if (this.currentPage < totalPages - 1) {
      this.currentPage++
      this.updateView()
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--
      this.updateView()
    }
  }

  updateView() {
    const start = this.currentPage * this.pageSizeValue
    const end = start + this.pageSizeValue
    const total = this.rowTargets.length
    const totalPages = Math.ceil(total / this.pageSizeValue)

    this.rowTargets.forEach((row, idx) => {
      row.classList.toggle("hidden", idx < start || idx >= end)
    })

    if (this.hasPageInfoTarget) {
      const showing = Math.min(end, total)
      this.pageInfoTarget.innerHTML = `${start + 1}&ndash;${showing} of ${total}`
    }

    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = this.currentPage === 0
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = this.currentPage >= totalPages - 1
  }
}
